/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';
import * as React from 'react';

import Retracked from 'js/app/retracked';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import localStorageEx from 'bundles/common/utils/localStorageEx';
import CourseraPlusNotificationBanner from 'bundles/coursera-plus-common/components/lihp/CourseraPlusNotificationBanner';
import ReactivateSubscriptionConfirmationModal from 'bundles/subscriptions-common/components/ReactivateSubscriptionConfirmationModal';
import ReactivateSubscriptionModal from 'bundles/subscriptions-common/components/ReactivateSubscriptionModal';
import {
  DISMISSED_SUBSCRIPTION_IDS_STORAGE_KEY,
  useEventTracking,
  useUndoCancellationDataForLihp,
} from 'bundles/subscriptions-common/utils/cancellationUndoUtils';

import _t from 'i18n!nls/subscriptions';

const STORAGE_KEY_PREFIX = 'canceled_subscription_reactivate_notification_banner_dismissed';

const TrackedButton = Retracked.withVisibilityTracking(withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button));

const styles = {
  reactivateWrapper: css`
    > div {
      display: inline-block;
    }
  `,
  reactivateLink: css`
    margin: 0 4px 0 10px;
    padding: 0;
    vertical-align: bottom;
    text-decoration: underline;

    ${breakpoints.down('xs')} {
      margin: 0 0 0 5px;
    }

    .cds-button-endIcon {
      margin-left: 4px;
    }
  `,
};

const CanceledSubscriptionReactivateNotification = () => {
  const [hideNotification, setHideNotification] = useState(false);
  const [showReactivateSubscriptionModal, setShowReactivateSubscriptionModal] = useState(false);
  const [showReactivateSubscriptionConfirmationModal, setShowReactivateSubscriptionConfirmationModal] = useState(false);

  const dismissedSubscriptionIds = localStorageEx.getItem(DISMISSED_SUBSCRIPTION_IDS_STORAGE_KEY, JSON.parse, []);

  const {
    showUndoCancellationCTA,
    subscription,
    s12nName = '',
    monthlySubscriptionPrice,
    restorationEligibilityEndsAt,
    nextBillingDate,
    eligibleSubscriptionIds,
    getCancelledStatusCopyOnLihp,
  } = useUndoCancellationDataForLihp({ dismissedSubscriptionIds });

  const { reactivateEventingV2Props, trackClickReactivateCta, useGetReactivateCtaTrackingRef } = useEventTracking(
    subscription,
    eligibleSubscriptionIds
  );
  const reactivateCtaTrackingRef = useGetReactivateCtaTrackingRef();

  if (!showUndoCancellationCTA || !subscription || !restorationEligibilityEndsAt) {
    return null;
  }

  const storageKey = STORAGE_KEY_PREFIX;
  const hasDismissedNotification = localStorageEx.getItem(storageKey, JSON.parse, false);

  if (hasDismissedNotification) {
    return null;
  }

  const handleBannerDismiss = () => {
    localStorageEx.setItem(
      DISMISSED_SUBSCRIPTION_IDS_STORAGE_KEY,
      [...dismissedSubscriptionIds, subscription.id],
      JSON.stringify
    );
    localStorageEx.removeItem(storageKey);
  };

  const toggleShowReactivateSubscriptionModal = () => {
    if (!showReactivateSubscriptionModal) {
      trackClickReactivateCta();
    }
    setShowReactivateSubscriptionModal(!showReactivateSubscriptionModal);
  };

  const handleReactivationSuccess = () => {
    setShowReactivateSubscriptionModal(false);
    setShowReactivateSubscriptionConfirmationModal(true);
  };

  const handleReactivationConfirmationClose = () => {
    setShowReactivateSubscriptionConfirmationModal(false);
    setHideNotification(true);
    localStorageEx.removeItem(storageKey);
  };

  const content = (
    <>
      <Typography2 component="h2" variant="subtitleMedium">
        {subscription.isCourseraPlusSubscription
          ? _t('Reactivate Coursera Plus')
          : _t('Reactivate your #{s12nName} subscription', { s12nName })}
      </Typography2>
      <Typography2 component="p" variant="bodySecondary" css={styles.reactivateWrapper}>
        {getCancelledStatusCopyOnLihp()}
        <TrackedButton
          css={styles.reactivateLink}
          size="small"
          variant="ghost"
          edgeAlign="start"
          icon={<ArrowNextIcon size="small" />}
          onClick={toggleShowReactivateSubscriptionModal}
          trackingName="reactivate_subscription_cta"
          trackingData={reactivateEventingV2Props}
          data={reactivateEventingV2Props}
        >
          <span ref={reactivateCtaTrackingRef}>{_t('Reactivate subscription')}</span>
        </TrackedButton>
      </Typography2>
    </>
  );

  return (
    <>
      <ReactivateSubscriptionModal
        isOpen={showReactivateSubscriptionModal}
        onReactivationSuccess={handleReactivationSuccess}
        onClose={toggleShowReactivateSubscriptionModal}
        monthlySubscriptionPrice={monthlySubscriptionPrice}
        subscription={subscription}
        nextBillingDate={nextBillingDate}
        eligibleSubscriptionIds={eligibleSubscriptionIds}
      />
      <ReactivateSubscriptionConfirmationModal
        isOpen={showReactivateSubscriptionConfirmationModal}
        onClose={handleReactivationConfirmationClose}
        subscription={subscription}
        s12nName={s12nName}
      />
      <CourseraPlusNotificationBanner
        content={content}
        storageKey={storageKey}
        hideNotification={hideNotification}
        onDismiss={handleBannerDismiss}
      />
    </>
  );
};

export default deferToClientSideRender(CanceledSubscriptionReactivateNotification);
